// src/components/Profile/Profile.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../Auth/AuthContext";
import { Link } from "react-router-dom";
import BASE_URL from "../../config/config";
import DateDisplay from "../common/DateDisplay";

// ======================
// کامپوننت‌های کوچک (بدون تغییر منطق)
// ======================

// Profile Header Component: Welcoming the user
const ProfileHeader = ({ name }) => (
  <div className="profile-header text-center mb-8">
    <h2 className="text-3xl font-bold text-gray-800">خوش آمدید، <span className="text-blue-600">{name}</span> 👋</h2>
    <p className="text-gray-500 mt-2">مدیریت پروفایل و تنظیمات کاربری</p>
  </div>
);

// Profile Picture Component
const ProfilePicture = ({ src, alt }) => (
  <div className="flex justify-center mb-6">
    <img
      src={src || "/images/avatar.jpg"}
      alt={alt}
      className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
    />
  </div>
);

// ======================
// کامپوننت اصلی پروفایل — با طراحی جدید
// ======================
const Profile = () => {
  const { user, updateUser } = useAuth();

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ ...user, password: "" });
  const [csrfToken, setCsrfToken] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch CSRF Token
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/csrf-token`, {
          credentials: "include",
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
      }
    };
    fetchCsrfToken();
  }, []);

  // Update form data when user changes
  useEffect(() => {
    setFormData({ ...user, password: "" });
  }, [user]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file selection for profile picture
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataWithFile = new FormData();
      formDataWithFile.append("username", formData.username);
      formDataWithFile.append("email", formData.email);
      formDataWithFile.append("phone", formData.phone);
      formDataWithFile.append("password", formData.password);
      formDataWithFile.append("first_name", formData.first_name);
      formDataWithFile.append("last_name", formData.last_name);
      selectedFile && formDataWithFile.append("profile_pic", selectedFile);

      const response = await fetch(`${BASE_URL}/user/update`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        body: formDataWithFile,
        credentials: "include",
      });

      if (response.ok) {
        setSuccessMessage("پروفایل با موفقیت به‌روزرسانی شد!");
        setErrorMessage("");

        const updatedUser = await response.json();
        updateUser(updatedUser);
        setEditMode(false);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.message || "خطایی در به‌روزرسانی رخ داده است.");
      }
    } catch (error) {
      setErrorMessage(error || "خطایی در به‌روزرسانی رخ داده است.");
    }
  };

  // Handle cancel editing
  const handleCancel = () => {
    setFormData(user);
    setEditMode(false);
  };

  return (
    <div className="min-h-screen  py-12 px- bg-light">
      <div className="max-w-6xl mx-auto ">
        {/* هدر خوش آمدگویی */}
        <ProfileHeader name={user?.first_name || "کاربر"} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* بخش اصلی پروفایل */}
          <div className="lg:col-span-2 space-y-8">
            {editMode ? (
              /* فرم ویرایش */
              <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">✏️ ویرایش پروفایل</h3>
                
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">عکس پروفایل</label>
                    <input
                      type="file"
                      name="profile_pic"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">نام کاربری</label>
                    <input
                      type="text"
                      name="username"
                      value={formData?.username || ""}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500 text-right focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email || ""}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData?.phone || ""}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData?.first_name || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نام خانوادگی</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData?.last_name || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور (اختیاری)</label>
                    <input
                      type="password"
                      name="password"
                      value={""}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoComplete="off"
                      placeholder="فقط در صورت تغییر رمز عبور وارد کنید"
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
                    >
                      💾 ذخیره تغییرات
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex-1 bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl hover:bg-gray-300 transition-colors"
                    >
                      ❌ لغو
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              /* نمایش پروفایل */
              <>
                {/* کارت پروفایل */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-200 text-center">
                  <ProfilePicture
                    src={user?.profile_pic || "/images/avatar.jpg"}
                    alt={user?.name || "User"}
                  />
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">پروفایل شما</h3>

                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 text-sm">
                      {successMessage}
                    </div>
                  )}

                  <div className="space-y-4 text-right">
                    <p><strong className="text-gray-700">نام کاربری:</strong> <span className="text-blue-600">{user?.username}</span></p>
                    <p><strong className="text-gray-700">ایمیل:</strong> <span className="text-gray-600">{user?.email}</span></p>
                    <p><strong className="text-gray-700">شماره تلفن:</strong> <span className="text-gray-600">{user?.phone}</span></p>
                    <p><strong className="text-gray-700">نام:</strong> <span className="text-gray-600">{user?.first_name}</span></p>
                    <p><strong className="text-gray-700">نام خانوادگی:</strong> <span className="text-gray-600">{user?.last_name}</span></p>
                    <p><strong className="text-gray-700">نقش:</strong> <span className="text-blue-500 font-medium">{user?.role}</span></p>
                    <p><strong className="text-gray-700">تاریخ عضویت:</strong> <span className="text-gray-600"><DateDisplay isoDate={user?.created_at} /></span></p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* سایدبار — عملیات سریع */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-6">⚡️ عملیات سریع</h3>
              
              <button
                onClick={() => setEditMode(true)}
                className="w-full btn-primary from-blue-500 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 mb-4"
              >
                ✏️ ویرایش پروفایل
              </button>

              <Link
                to="/dashboard"
                className="w-full block btn-secondary from-gray-500 to-gray-600 text-white font-bold py-3 px-4 rounded-xl text-center shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                🏠 بازگشت به داشبورد
              </Link>
            </div>

            {/* کارت اطلاعات */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-700 mb-4">💡 راهنمایی</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• برای تغییر رمز عبور، فیلد را پر کنید.</li>
                <li>• عکس پروفایل باید JPG/PNG باشد.</li>
                <li>• بعد از ذخیره، صفحه رفرش می‌شود.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;